import { XMarkIcon } from "@heroicons/react/20/solid";
import { connect } from "react-redux";
import { addMonths, format } from "date-fns";

import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";

import EmployeeRow from "../EmployeeRow";
import cleanDeep from "clean-deep";
import cloneDeep from "lodash/cloneDeep";

const BulkChangesModal = ({
	isOpen,
	setBulkChangesModal,
	hasDepartments,
	employees,
	confirmModal,
	confirmChange,
	employeesGetListWithSalary,
	effectiveDate,
}) => {
	if (!isOpen) {
		return null;
	}

	const applyChanges = () => {
		//Go through employees and apply changes
		for (const i in employees) {
			const employee = employees[i];

			const salary = employee?.salaries[employee?.salaries.length - 1];

			//Push the changes to the server
			confirmModal({
				input: cleanDeep(employee?.salaries[0], {
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				}),
				status: true,
				formType: "Salary",
				previousDate: salary?.start,
			});

			// TODO consider using some other function that handles effective dates that are before the current salary start date.
			const dateToUse = effectiveDate < salary.start ? salary.start : effectiveDate;

			confirmChange(dateToUse, true, salary.agreement);
		}

		//Update the employee list after a short delay
		setTimeout(() => {
			employeesGetListWithSalary({
				from: format(addMonths(new Date(), -2), "yyyy-MM-01"),
				to: format(new Date(), "yyyy-12-31"),
			});
		}, 2000);

		//Close the modal
		closeModal();
	};

	const closeModal = () => {
		setBulkChangesModal(false);
	};

	return (
		<>
			<div
				className="modal"
				tabIndex="-1"
				aria-hidden="true"
				style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
			>
				<div
					className="modal-dialog"
					style={{ width: "75%" }}
				>
					<div className="modal-content">
						<div
							className="block block-themed block-transparent remove-margin-b"
							style={{ padding: 0 }}
						>
							<div
								className="block-header bg-primary-dark"
								style={{ padding: 20 }}
							>
								<ul className="block-options">
									<li>
										<button
											type="button"
											onClick={() => {
												closeModal();
											}}
										>
											<XMarkIcon className="h-6 w-6 mr-3 text-white" />
										</button>
									</li>
								</ul>
								<h3 className="block-title">Verify Bulk Changes</h3>
							</div>
						</div>
						<div className="block-content">
							<div className="content">
								<h3 style={{ padding: 5, marginBottom: 10 }}>Employees With Proposed Changes taking effect on {format(new Date(effectiveDate),"do MMM yyyy")}</h3>
								<div
									className="table-responsive"
									style={{ overflow: "auto" }}
								>
									<div
										id="active-people-table_wrapper"
										className="dataTables_wrapper no-footer"
									>
										<table
											id="active-people-table"
											className="js-table-sections table table-hover"
										>
											<thead style={{ display: "table-header-group" }}>
												<tr>
													<th></th>
													<th>NAME</th>
													<th>D.O.E</th>
													<th>EMPLOYMENT TYPE</th>
													{hasDepartments ? <th>DEPARTMENT</th> : null}
													<th>ROLE</th>
													<th>SALARY</th>
													<th></th>
												</tr>
											</thead>
											<tbody>
												{employees?.map((employee) => {
													return (
														<EmployeeRow
															key={employee?.id}
															employee={employee}
															fromBulkChanges={true}
															salaryVisibility={true}
														/>
													);
												})}
											</tbody>
										</table>
									</div>
								</div>
								<button
									className="btn btn-danger"
									type="button"
									style={{ marginBottom: 10 }}
									onClick={() => closeModal()}
								>
									<span>Cancel</span>
								</button>
								<button
									className="btn btn-success pull-right"
									type="button"
									style={{ marginBottom: 10 }}
									onClick={() => applyChanges()}
								>
									{employees?.length == 1 ? (
										<span>Apply 1 change</span>
									) : (
										<span>{`Apply ${employees?.length} changes effective ${format(new Date(effectiveDate),"do MMM yyyy")}`}</span>
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { enableDepartments, departments } = state?.company?.settings;

	const {employees: originalEmployees, changes} = ownProps

	let employees = cloneDeep(originalEmployees);

	employees.map((employee) => {
		// spread to avoid modifying the same object
		employee = {...employee};
		// apply all the changes,
		for (const i in changes) {
			let changed = false;
			const change = changes[i];
			const salary = employee?.salaries[employee?.salaries.length - 1];

			//Check if the change is an allowance, if so, update the allowance
			if (change?.selectedFilter?.value.includes("allowance")) {
				const allowances = salary?.allowances;

				const existingAllowance = allowances.find((a) => a?.type == change?.selectedFilter?.value.split("-").pop());

				let amount = change?.value;
				if (change.secondaryValue == "percentage") {
					amount = parseFloat((existingAllowance?.amount ?? 0) * (change?.value / 100).toFixed(4));
				}

				//If allowance already exists, update the amount, else create a new allowance
				if (existingAllowance) {
					existingAllowance.amount = amount;
				} else {
					if (amount > 0) {
						allowances[allowances.length] = {
							amount: change?.value,
							type: change?.selectedFilter?.value.split("-").pop(),
						};
					}
				}

				changed = true;
			}

			let amount = change?.value;
			if (change?.secondaryValue == "percentage") {
				amount = (salary[change?.selectedFilter?.value] ?? 0) * (change?.value / 100);
			}

			if (!changed) salary[change?.selectedFilter?.value] = amount;
		}
	})

	return {
		employees,
		isOpen: state.employees?.bulkChangesModalOpen,
		hasDepartments: enableDepartments === undefined ? !!departments?.length : enableDepartments,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setBulkChangesModal: (open) => dispatch(EMPLOYEEACTIONS.setBulkChangesModal(open)),
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
	confirmChange: (date, replace, agreement, notification) =>
		dispatch(EMPLOYEEACTIONS.confirmChange({ date, replace, agreement, notification })),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkChangesModal);
